import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {PaymentMethods} from '../../models/payment-method.model';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {AdditionalPACData, PaymentFrequency} from '../../models/additional-pac-data.model';
import {PacService} from '../../shared/services/pac.service';
import {PaymentBlockService} from '../../services/payment-block.service';
import {AppConstants} from '../../shared/app-constants';
import {getDefaultPaymentAmount, restrictToTwoDecimals} from '../../shared/utils/default-amount-utils';

@Component({
  selector: 'app-credit-debit-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './credit-debit-card.component.html',
  styleUrl: './credit-debit-card.component.scss',
})
export class CreditDebitCardComponent implements OnInit, OnChanges {
  @Input() isSelected: boolean = false;
  @Input() paymentMethodInfo!: PaymentMethods;
  @Output() cardClick = new EventEmitter<string | null>();
  @Input() pacDetails!: AdditionalPACData;
  @Input() selectedPacCount!: number;


  constructor(private readonly pacService: PacService, private readonly paymentBlockService: PaymentBlockService) {
  }

  enteredAmount: number | null = null;
  disablePayButton: boolean = false;
  messageToShow: string = '';
  loading = false;
  error: string = '';
  creditCardAmount: number | undefined = 0;
  enableAmountCheckbox: boolean = false;
  isInputEnabled: boolean = false;
  isCreditFocused: boolean = false;
  amountZero: boolean = false;

  onSelectMethod(paymentMethod: string | null) {
    this.isSelected = true;
    this.cardClick.emit(paymentMethod);
  }

  ngOnInit() {
    this.creditCardAmount = getDefaultPaymentAmount(this.pacDetails);
    this.validateAmount(this.creditCardAmount);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pacDetails']?.currentValue != null) {
      this.creditCardAmount = this.pacDetails.balancePayable;
      this.validateAmount(this.creditCardAmount);
    }
  }

  inputAmountFormatted(value: number | null | undefined): string | undefined {
    return AppConstants.inputAmountFormatted(value);
  }

  showAmountFormatted(value: number | null | undefined): string {
    return AppConstants.showAmountFormatted(value);
  }

  onAmountChange(event: Event) {
    const sanitizedValue = restrictToTwoDecimals(event);
    this.creditCardAmount = sanitizedValue ? Number.parseFloat(sanitizedValue) : 0;
    this.validateAmount(this.creditCardAmount);
  }

  onCreditAmountBlur(event: FocusEvent) {
    this.isCreditFocused = false;
  }

  private validateAmount(amount: number | null): void {
    this.enteredAmount = amount;
    this.disablePayButton = false;
    this.messageToShow = '';
    this.amountZero = false;
    if (amount != undefined && amount <= 0) {
      this.amountZero = true;
    }
    if (amount !== null) {
      if (amount > this.pacDetails.balancePayable) {
        this.disablePayButton = true;
        this.messageToShow = 'PAYABLE_EXCEEDED';
      } else if (this.remainingLimit !== 0 && this.remainingLimit !== null && amount > this.remainingLimit) {
        this.disablePayButton = true;
        this.messageToShow = 'BALANCE_LIMIT_EXCEEDED';
      }
    }
  }


  onPay() {
    const paymentData = {
      amount: this.enteredAmount != null && Number((this.enteredAmount).toFixed(2)),
      pacNumber: this.pacDetails.pacNumber
    };
    this.pacService.initiatePayment(paymentData).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.redirectToPayment(response.paymentUrl, response.formData);
        } else if (response.blocked) {
          this.paymentBlockService.notifyBlocked(this.pacDetails.pacNumber, response.retryAfterSeconds);
        } else {
          this.error = response.message || 'Payment initiation failed';
        }
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Network error. Please try again.';
      }
    });
  }

  private redirectToPayment(paymentUrl: string, formData: any) {
    (globalThis as any).__intentionalRedirect__ = true;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentUrl;

    Object.keys(formData).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = formData[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  toggleAmountInput(event: any) {
    const input = event.target as HTMLInputElement;
    this.isInputEnabled = input.checked;
    this.enableAmountCheckbox = input.checked;
    if (!input.checked) {
      this.creditCardAmount = this.pacDetails.paymentFrequency === PaymentFrequency.I
        ? this.pacDetails?.selectedAmountPayable
        : this.pacDetails?.balancePayable;
      this.validateAmount(Number(this.creditCardAmount));
    }
  }

  get limitLabel(): string {
    let paymentFrequency = !this.pacDetails?.paymentFrequency;
    return paymentFrequency ? 'transaction' : 'Payment Advice/Invoice';
  }

  get totalLimit(): number {
    return this.paymentMethodInfo?.totalLimit ?? 0;
  }

  get remainingLimit(): number {
    return this.paymentMethodInfo.remainingLimit ?? 0;
  }

  handleDropdownKey(event: any) {
    return AppConstants.handleDropdownKey(event);
  }
}
